// frontend/src/pages/PaymentHistory/components/PaymentRow.tsx
// One <tr> for a payment: date, obligation, method, amount.
// Owns its hover state locally; parent passes only the data.

import React, { useState } from "react";
import { Building2 } from "lucide-react";
import type { Payment } from "../types";
import { fmtDate, fmtMoney, obligationLabel, subLabel, methodLabel } from "../helpers";
import { T, MONO } from "../styles";

interface PaymentRowProps {
  payment: Payment;
  /** True for the first row in the table (suppresses the top border). */
  first: boolean;
}

export default function PaymentRow({
  payment: p,
  first,
}: PaymentRowProps): React.ReactElement {
  const [hover, setHover] = useState<boolean>(false);

  const borderTop = first ? "none" : "1px solid " + T.line;

  return (
    <tr
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: "default",
        background: hover ? T.cardAlt : "transparent",
        transition: "background .1s",
      }}
    >
      {/* Date */}
      <td
        style={{
          padding: "14px 16px",
          fontSize: 12.5,
          color: T.muted,
          fontWeight: 500,
          borderTop,
        }}
      >
        {fmtDate(p.payment_date)}
      </td>

      {/* Obligation (label + sub) */}
      <td
        style={{
          padding: "14px 16px",
          fontSize: 13.5,
          color: T.ink,
          borderTop,
        }}
      >
        <div style={{ fontWeight: 600 }}>{obligationLabel(p)}</div>
        <div style={{ color: T.muted, marginTop: 2, fontSize: 12.5, fontWeight: 500 }}>
          {subLabel(p)}
        </div>
      </td>

      {/* Method */}
      <td style={{ padding: "14px 16px", borderTop }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12.5,
            color: T.slate,
            fontWeight: 600,
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              background: T.line,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: T.slate,
            }}
          >
            <Building2 size={12} />
          </span>
          {methodLabel(p)}
        </span>
      </td>

      {/* Amount */}
      <td
        style={{
          padding: "14px 16px",
          textAlign: "right",
          fontFamily: MONO,
          fontSize: 13.5,
          fontWeight: 600,
          color: T.ink,
          borderTop,
        }}
      >
        {fmtMoney(p.amount)}
      </td>
    </tr>
  );
}